// Implements: F-UIX-018, F-RPT-009
/**
 * "Exportar" on Mi consumo (owner 2026-10-19): a format and one download per report of the page
 * (the caller's consumption and their cost per mockup), as the server's export twins serve them.
 * Pure presentation: the format, the report being downloaded and the last refusal are the
 * container's. A section of the page (`h2`), like the page's other sections.
 */

import { useId } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import type { ExportFormat, MyUsageExport } from "../../../types/reports";
import { EXPORT_FORMATS } from "../model/scopeExports";
import styles from "./MyUsage.module.css";

const REPORTS: readonly MyUsageExport[] = ["consumption", "mockups"];

const LABELS = {
  consumption: "backoffice.myUsage.exports.consumption",
  mockups: "backoffice.myUsage.exports.mockups",
} as const satisfies Record<MyUsageExport, string>;

export interface MyUsageExportsProps {
  readonly format: ExportFormat;
  /** The report being downloaded, or `null`. */
  readonly downloading: MyUsageExport | null;
  /** The last download's refusal as the person reads it, or `null`. */
  readonly failure: string | null;
  readonly onFormat: (format: ExportFormat) => void;
  readonly onDownload: (report: MyUsageExport) => void;
}

export function MyUsageExports({ format, downloading, failure, onFormat, onDownload }: MyUsageExportsProps): ReactElement {
  const { t } = useTranslation();
  const ids = useId();
  const headingId = `${ids}-title`;
  const formatId = `${ids}-format`;

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.sectionTitle}>
        {t("backoffice.exports.title")}
      </h2>
      <p className={styles.hint}>{t("backoffice.myUsage.exports.intro")}</p>
      <p>
        <label htmlFor={formatId}>{t("backoffice.exports.format")}</label>{" "}
        <select
          id={formatId}
          value={format}
          onChange={(event) => {
            onFormat(event.target.value as ExportFormat);
          }}
        >
          {EXPORT_FORMATS.map((option) => (
            <option key={option} value={option}>
              {t(`backoffice.exports.format.${option}`)}
            </option>
          ))}
        </select>
      </p>
      {REPORTS.map((report) => (
        <div key={report}>
          <Button
            variant="secondary"
            type="button"
            loading={downloading === report}
            disabled={downloading !== null}
            onClick={() => {
              onDownload(report);
            }}
          >
            {t(LABELS[report])}
          </Button>
        </div>
      ))}
      {failure === null ? null : (
        <p className={styles.note} role="alert">
          {failure}
        </p>
      )}
    </section>
  );
}
